<template>
  <div class="page-layout">
    <Sidebar :class="{ open: isSidebarOpen }" @toggle-sidebar="toggleSidebar" />
    <div class="addons-container">
      <div class="header">
        <div class="header-left">
          <button class="menu-toggle" @click="toggleSidebar" aria-label="Toggle Menu">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M3 12h18M3 6h18M3 18h18"/>
            </svg>
          </button>
          <h1>Addons</h1>
        </div>
        <div class="header-actions">
          <button @click="refreshData" class="refresh-btn">
            Refresh Data
          </button>
          <button @click="createAddon" class="create-btn">
            Create Addon
          </button>
        </div>
      </div>

      <div class="table-container">
        <div v-if="loading" class="loading-container">
          <div class="loading-spinner"></div>
          <p>Loading addons...</p>
        </div>
        <table v-else class="addons-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Name</th>
              <th>Description</th>
              <th>Price</th>
              <th>Status</th>
              <th>Created At</th>
              <th>Updated At</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="addon in addons" :key="addon.id">
              <td>{{ addon.id }}</td>
              <td>{{ addon.name }}</td>
              <td>{{ addon.description }}</td>
              <td>${{ addon.price }}</td>
              <td>
                <span :class="['status-badge', addon.is_active ? 'active' : 'inactive']">
                  {{ addon.is_active ? 'Active' : 'Inactive' }}
                </span>
              </td>
              <td>{{ formatDate(addon.created_at) }}</td>
              <td>{{ formatDate(addon.updated_at) }}</td>
              <td class="actions-cell">
                <button @click="viewAddon(addon.id)" class="action-btn view-btn">
                  View
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="pagination">
        <button 
          :disabled="currentPage === 1" 
          @click="changePage(currentPage - 1)"
          class="pagination-btn"
        >
          Previous
        </button>
        <span class="page-info">
          Page {{ currentPage }} of {{ lastPage }}
        </span>
        <button 
          :disabled="currentPage === lastPage" 
          @click="changePage(currentPage + 1)"
          class="pagination-btn"
        >
          Next
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import axios from 'axios';
import Sidebar from '@/components/Sidebar.vue';

export default {
  name: 'AddonsManager',
  components: {
    Sidebar
  },
  setup() {
    const router = useRouter();
    const addons = ref([]);
    const currentPage = ref(1);
    const lastPage = ref(1);
    const loading = ref(false);
    const isSidebarOpen = ref(false);

    const toggleSidebar = () => {
      isSidebarOpen.value = !isSidebarOpen.value;
    };

    const fetchAddons = async (page = 1) => {
      try {
        loading.value = true;
        const token = localStorage.getItem('token');
        
        if (!token) {
          throw new Error('No token found');
        }

        const response = await axios.get('https://api.kewaar.com/api/admin/addons', {
          headers: {
            'Authorization': `Bearer ${token}`
          },
          params: {
            page
          }
        });

        if (response.data.success) {
          addons.value = response.data.data.data;
          currentPage.value = response.data.data.current_page;
          lastPage.value = response.data.data.last_page;
        }
      } catch (error) {
        console.error('Error fetching addons:', error);
      } finally {
        loading.value = false;
      }
    };

    const changePage = (page) => {
      if (page >= 1 && page <= lastPage.value) {
        fetchAddons(page);
      }
    };

    const refreshData = () => {
      fetchAddons(currentPage.value);
    };

    const formatDate = (dateString) => {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    };

    const createAddon = () => {
      router.push('/addons/create');
    };

    const viewAddon = (id) => {
      router.push(`/addons/${id}`);
    };

    onMounted(() => {
      fetchAddons();
    });

    return {
      addons,
      currentPage,
      lastPage,
      loading,
      changePage,
      refreshData,
      formatDate,
      isSidebarOpen,
      toggleSidebar,
      createAddon,
      viewAddon
    };
  }
};
</script>

<style scoped>
.page-layout {
  display: flex;
  min-height: 100vh;
  width: 100%;
}

.addons-container {
  flex: 1;
  padding: 2rem;
  min-width: 0;
  width: 100%;
  margin-left: 280px; /* Match sidebar width */
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.header-actions {
  display: flex;
  gap: 1rem;
}

.create-btn {
  padding: 0.5rem 1rem;
  background-color: #1976d2;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.3s;
  text-decoration: none;
}

.create-btn:hover {
  background-color: #1565c0;
}

.header h1 {
  margin: 0;
  font-size: 1.8rem;
  color: #2c3e50;
}

.refresh-btn {
  padding: 0.5rem 1rem;
  background-color: #4CAF50;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.3s;
}

.refresh-btn:hover {
  background-color: #45a049;
}

.table-container {
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  overflow-x: auto;
  width: 100%;
}

.addons-table {
  width: 100%;
  border-collapse: collapse;
  min-width: 800px;
}

.addons-table th,
.addons-table td {
  padding: 1rem;
  text-align: left;
  border-bottom: 1px solid #eee;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.addons-table th {
  background-color: #f8f9fa;
  font-weight: 600;
  color: #2c3e50;
}

.addons-table td:nth-child(3) {
  white-space: normal;
  max-width: 200px;
}

.status-badge {
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.875rem;
  font-weight: 500;
}

.status-badge.active {
  background-color: #e3f2fd;
  color: #1976d2;
}

.status-badge.inactive {
  background-color: #fce4ec;
  color: #c2185b;
}

.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 2rem;
  gap: 1rem;
}

.pagination-btn {
  padding: 0.5rem 1rem;
  background-color: #1976d2;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.3s;
}

.pagination-btn:disabled {
  background-color: #ccc;
  cursor: not-allowed;
}

.pagination-btn:not(:disabled):hover {
  background-color: #1565c0;
}

.page-info {
  font-size: 0.875rem;
  color: #666;
}

.menu-toggle {
  display: none;
  background: none;
  border: none;
  padding: 0.5rem;
  cursor: pointer;
  color: #2c3e50;
}

.menu-toggle svg {
  width: 24px;
  height: 24px;
}

.actions-cell {
  display: flex;
  gap: 0.5rem;
}

.action-btn {
  padding: 0.25rem 0.5rem;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.3s;
  font-size: 0.875rem;
}

.view-btn {
  background-color: #1976d2;
  color: white;
}

.view-btn:hover {
  background-color: #1565c0;
}

@media (max-width: 768px) {
  .addons-container {
    margin-left: 0;
    padding: 1rem;
  }

  .header {
    flex-direction: column;
    gap: 1rem;
    align-items: flex-start;
  }

  .header-left {
    display: flex;
    align-items: center;
    gap: 1rem;
  }

  .header-actions {
    width: 100%;
  }

  .create-btn {
    width: 100%;
    text-align: center;
  }

  .actions-cell {
    flex-direction: column;
  }

  .action-btn {
    width: 100%;
  }

  .menu-toggle {
    display: block;
  }

  .table-container {
    margin: 0 -1rem;
    width: calc(100% + 2rem);
    border-radius: 0;
  }

  .addons-table {
    font-size: 0.875rem;
  }

  .addons-table th,
  .addons-table td {
    padding: 0.75rem 0.5rem;
  }

  .pagination {
    flex-wrap: wrap;
  }

  .status-badge {
    padding: 0.25rem 0.5rem;
    font-size: 0.75rem;
  }
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem;
  text-align: center;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 3px solid #f3f3f3;
  border-top: 3px solid #1976d2;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 1rem;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}
</style> 